// Onde marine ANIMATE v2 — port dell'animazione v1 (_drawAnimatedWaves,
// map-legend.js:1106-1173): creste bianche sinusoidali su una griglia, con
// ampiezza/opacità dalla forza dell'onda e fase che avanza nel tempo.
//
// In v1 la forza per cella veniva dal campo IDW marino (parametro 'onda'); in v2
// i parametri marini stazione sono stati rimossi dal selettore → qui la forza si
// ricava interpolando (IDW gaussiano) l'altezza d'onda delle BOE ondametriche.
// Tutto su un canvas a DOMINIO FISSO (come interpol.js) montato come source
// MapLibre type 'canvas' animate:true: pan/zoom sono GPU, l'animazione gira in
// un RAF che ridisegna solo il canvas 1100×900. La terra è forata dalla maschera
// Sicilia condivisa → le creste restano solo sul mare (come punchSicilyLand v1).
import { DOMAIN, DOMAIN_COORDS, domainPoint, compositeSicilyMask, loadSicilyFeature } from './interpol.js';

const SRC = 'ws-waveanim';
const LYR = 'ws-waveanim-layer';
const SPACING = 26;   // px (dominio) tra una cresta e l'altra
const IDW_SIGMA = 210; // px: raggio d'influenza gaussiano delle boe

const state = {
    map: null, active: false,
    canvas: null, ctx: null, cells: null, raf: null,
    buoys: [], _timer: null,
};

function ensureCanvas() {
    if (state.canvas) return;
    const c = document.createElement('canvas');
    c.width = DOMAIN.width; c.height = DOMAIN.height;
    state.canvas = c;
    state.ctx = c.getContext('2d', { alpha: true });
}

// Griglia di creste con forza IDW dalle boe (una volta per refresh dati: dominio
// fisso → invariante a pan/zoom, esattamente come le celle v1 in _waveCellCache).
function buildCells() {
    const buoys = (state.buoys || []).map(b => {
        const lat = Number(b.lat ?? b.latitude);
        const lng = Number(b.lon ?? b.lng ?? b.longitude);
        if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
        const p = domainPoint(lat, lng);
        const h = Number(b.wave_height_m ?? b.wave_height ?? b.significant_wave_height);
        return { x: p.x, y: p.y, h: Number.isFinite(h) ? h : null };
    }).filter(Boolean);

    const cells = [];
    const sigma2 = IDW_SIGMA * IDW_SIGMA;
    for (let y = 0; y <= DOMAIN.height; y += SPACING) {
        for (let x = 0; x <= DOMAIN.width; x += SPACING) {
            let cov = 0, wsum = 0, hsum = 0;
            for (const b of buoys) {
                const dx = x - b.x, dy = y - b.y;
                const w = Math.exp(-(dx * dx + dy * dy) / (2 * sigma2));
                cov += w;
                if (b.h !== null) { wsum += w; hsum += w * b.h; }
            }
            if (cov < 0.02) continue; // lontano da ogni boa → niente creste
            const waveH = wsum > 0 ? hsum / wsum : 0.4;
            const strength = Math.max(0.10, Math.min(1, waveH / 2.8));   // formula v1
            const coverage = Math.min(1, cov);
            const alpha = Math.min(0.72, Math.pow(Math.min(1, coverage * 1.75), 0.72) * 0.68);
            if (alpha < 0.04) continue;
            cells.push({
                x, y,
                alpha: alpha * (0.46 + strength * 0.42),
                lineWidth: 1.05 + strength * 1.45,
                length: SPACING * (0.82 + strength * 0.48),
                amplitude: 3.2 + strength * 5.8,
                phaseBase: (x * 0.035) + (y * 0.024),
            });
        }
    }
    state.cells = cells;
}

function drawFrame(now) {
    const ctx = state.ctx;
    if (!ctx || !state.cells) return;
    ctx.clearRect(0, 0, DOMAIN.width, DOMAIN.height);
    ctx.save();
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.strokeStyle = 'rgba(255,255,255,0.92)';
    const phaseTime = now / 520;   // v1
    for (const c of state.cells) {
        const phase = c.phaseBase + phaseTime;
        ctx.globalAlpha = c.alpha;
        ctx.lineWidth = c.lineWidth;
        ctx.beginPath();
        ctx.moveTo(c.x - c.length / 2, c.y + Math.sin(phase) * c.amplitude);
        ctx.quadraticCurveTo(
            c.x, c.y - c.amplitude + Math.cos(phase * 0.9) * c.amplitude,
            c.x + c.length / 2, c.y + Math.sin(phase + Math.PI) * c.amplitude
        );
        ctx.stroke();
    }
    ctx.restore();
    // fora la terra: le creste restano solo sul mare (v1 punchSicilyLandFromCanvas)
    compositeSicilyMask(ctx, 'destination-out');
}

function loop() {
    if (!state.active) return;
    drawFrame(performance.now());
    state.raf = requestAnimationFrame(loop);
}

async function refreshData() {
    try { state.buoys = await window.WSWeatherApi.getBuoys().catch(() => []); }
    catch (e) { state.buoys = []; }
    buildCells();
}

export function initWaveAnim(map) {
    state.map = map;
}

export async function setWaveAnimEnabled(on) {
    const map = state.map;
    on = !!on;
    if (!map || on === state.active) return;
    if (on) {
        state.active = true;
        ensureCanvas();
        try { await loadSicilyFeature(); } catch (e) { /* la maschera degrada a no-op */ }
        await refreshData();
        if (!state.active) return; // spento durante il load
        if (!map.getSource(SRC)) {
            map.addSource(SRC, { type: 'canvas', canvas: state.canvas, coordinates: DOMAIN_COORDS, animate: true });
            const before = map.getLayer('ws-stations-symbols') ? 'ws-stations-symbols' : undefined;
            map.addLayer({ id: LYR, type: 'raster', source: SRC, paint: { 'raster-opacity': 0.92, 'raster-fade-duration': 0 } }, before);
        }
        if (!state._timer) state._timer = setInterval(refreshData, 10 * 60 * 1000);
        loop();
    } else {
        state.active = false;
        if (state.raf) { cancelAnimationFrame(state.raf); state.raf = null; }
        if (state._timer) { clearInterval(state._timer); state._timer = null; }
        if (map.getLayer(LYR)) map.removeLayer(LYR);
        if (map.getSource(SRC)) map.removeSource(SRC);
    }
}
